import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "browsing-controls",
  class BrowsingControls extends WYATTElement {
    SELECTORS = {
      originalButton:
        ".browsing-controls__filter-button:not(.browsing-controls__filter-button--sticky)",
      stickyContainer: ".browsing-controls__sticky-container",
      footer: "#page-footer",
      backToTop: "[data-back-to-top]",
      productGrid: ".collection__products",
      drawer: "dialog",
    };

    MOBILE_BREAKPOINT = 1024;
    SCROLL_OFFSET = 20;

    constructor() {
      super();

      this.stickyContainer = this.querySelector(this.SELECTORS.stickyContainer);
      this.originalButton = this.querySelector(this.SELECTORS.originalButton);
      this.backToTopButton = this.querySelector(this.SELECTORS.backToTop);

      if (!this.stickyContainer || !this.originalButton) {
        return;
      }

      this.init();
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener("resize", this.handleResize);
    }

    init() {
      if (!this.isMobileView()) return;

      this.initializeObservers();
      this.setupBackToTop();
      this.setupScrollToControls();
    }

    isMobileView() {
      return window.innerWidth < this.MOBILE_BREAKPOINT;
    }

    handleResize() {
      const isMobile = this.isMobileView();
      const hasObservers = !!this.buttonObserver;

      if (isMobile && !hasObservers) {
        this.initializeObservers();
      } else if (!isMobile && hasObservers) {
        this.cleanupObservers();
      }
    }

    initializeObservers() {
      this.observeOriginalButton();
      this.observeFooter();
    }

    observeOriginalButton() {
      this.buttonObserver = new IntersectionObserver((entries) => {
        const isVisible = entries[0].isIntersecting;
        if (isVisible) {
          this.hideStickyButton();
        } else {
          this.showStickyButton();
        }
      });

      this.buttonObserver.observe(this.originalButton);
    }

    observeFooter() {
      const footer = document.querySelector(this.SELECTORS.footer);
      if (!footer) return;

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          const isVisible = entries[0].isIntersecting;
          if (isVisible) {
            this.hideStickyButton();
          } else if (!this.isElementVisible(this.originalButton)) {
            this.showStickyButton();
          }
        },
        { threshold: 0.1 },
      );

      this.footerObserver.observe(footer);
    }

    setupBackToTop() {
      this.backToTopButton.addEventListener("click", () => {
        this.scrollTo(0);
      });
    }

    setupScrollToControls() {
      this.subscribe(WYATT.PubSub.events.filtersUpdated, () => {
        const drawer = document.querySelector(this.SELECTORS.drawer);
        const scrollAction = () => this.scrollToControls();

        if (drawer?.open) {
          drawer.addEventListener("close", scrollAction, { once: true });
        } else {
          scrollAction();
        }
      });
    }

    scrollToControls() {
      const elementPosition = this.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - this.SCROLL_OFFSET;

      this.scrollTo(offsetPosition);
    }

    scrollTo(position) {
      window.scrollTo({
        top: position,
        behavior: "smooth",
      });
    }

    isElementVisible(element) {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom >= 0;
    }

    shouldShowStickyButton() {
      const productGrid = document.querySelector(this.SELECTORS.productGrid);
      return productGrid && productGrid.offsetHeight > window.innerHeight;
    }

    showStickyButton() {
      if (this.shouldShowStickyButton()) {
        this.stickyContainer.setAttribute("visible", "true");
      }
    }

    hideStickyButton() {
      this.stickyContainer.removeAttribute("visible");
    }

    cleanupObservers() {
      this.buttonObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.buttonObserver = null;
      this.footerObserver = null;
      this.hideStickyButton();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      window.removeEventListener("resize", this.handleResize);
      this.cleanupObservers();
    }
  },
);
